import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { WEBINAR_CONFIG } from "../../config/webinar";

interface MusicMenuItem {
  label: string;
  to: string;
}

// Add new items here to extend the Music dropdown — desktop and mobile both
// render from this one list, so nothing else needs to change.
const MUSIC_MENU_ITEMS: MusicMenuItem[] = [
  { label: "Latest Release", to: "/listen" },
  { label: "Music Videos", to: "/music" },
];

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false); // mobile menu
  const [musicMenuOpen, setMusicMenuOpen] = useState(false); // desktop dropdown
  const [mobileMusicOpen, setMobileMusicOpen] = useState(false); // mobile submenu
  const musicMenuRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();

  // Close the desktop dropdown on outside click (covers the click-to-open case —
  // hover-close already handles the hover case).
  useEffect(() => {
    if (!musicMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (musicMenuRef.current && !musicMenuRef.current.contains(e.target as Node)) {
        setMusicMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [musicMenuOpen]);

  const closeMobileMenu = () => {
    setIsOpen(false);
    setMobileMusicOpen(false);
  };

  return (
    <nav className={`bg-gradient-to-r ${currentTheme.colors.primary} shadow-2xl sticky top-0 z-50 backdrop-blur-lg bg-opacity-95`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/" className="text-white">
              <div className="flex flex-col font-sans">
                <span className="text-xl font-extrabold tracking-tight">
                  Mukesh Rathore<sup className="text-xs font-normal ml-0.5">TM</sup>
                </span>
                <span className="text-sm font-light tracking-wide">Original Artist Builder - India's #1 Indie Music Coach</span>

              </div>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-white hover:text-purple-200 font-semibold transition-colors">
              Home
            </Link>
            <Link to="/about" className="text-white hover:text-purple-200 font-semibold transition-colors">
              About
            </Link>

            {/* Music dropdown */}
            <div
              ref={musicMenuRef}
              className="relative"
              onMouseEnter={() => setMusicMenuOpen(true)}
              onMouseLeave={() => setMusicMenuOpen(false)}
            >
              <button
                onClick={() => setMusicMenuOpen((open) => !open)}
                aria-haspopup="true"
                aria-expanded={musicMenuOpen}
                className="flex items-center gap-1 text-white hover:text-purple-200 font-semibold transition-colors"
              >
                Music
                <ChevronIcon open={musicMenuOpen} />
              </button>

              {musicMenuOpen && (
                <div className="absolute top-full left-0 pt-2 w-52">
                  <div className="bg-white rounded-lg shadow-2xl overflow-hidden py-2">
                    {MUSIC_MENU_ITEMS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMusicMenuOpen(false)}
                        className="block px-4 py-2.5 text-gray-700 hover:bg-purple-50 hover:text-purple-700 font-semibold transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/tutorial" className="text-white hover:text-purple-200 font-semibold transition-colors">
              Tutorial
            </Link>
            <Link to="/blog" className="text-white hover:text-purple-200 font-semibold transition-colors">
              Blog
            </Link>
            <Link to="/moments" className="text-white hover:text-purple-200 font-semibold transition-colors">
              Moments
            </Link>
            <a href="/#faq" className="text-white hover:text-purple-200 font-semibold transition-colors">
              FAQ
            </a>
            <button
              onClick={() => window.open(WEBINAR_CONFIG.url, '_blank')}
              className="bg-white text-purple-900 hover:bg-purple-100 font-bold py-2 px-6 rounded-lg transition-all shadow-lg"
            >
              {WEBINAR_CONFIG.text}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-purple-200 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4">
            <Link
              to="/"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              About
            </Link>

            {/* Music expandable submenu */}
            <div>
              <button
                onClick={() => setMobileMusicOpen((open) => !open)}
                aria-expanded={mobileMusicOpen}
                className="w-full flex items-center justify-between py-2 text-white hover:text-purple-200 font-semibold"
              >
                Music
                <ChevronIcon open={mobileMusicOpen} />
              </button>
              {mobileMusicOpen && (
                <div className="pl-4 border-l border-white/20 ml-1 mb-1">
                  {MUSIC_MENU_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="block py-2 text-white/90 hover:text-purple-200 font-medium"
                      onClick={closeMobileMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/tutorial"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              Tutorial
            </Link>
            <Link
              to="/blog"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              Blog
            </Link>
            <Link
              to="/moments"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              Moments
            </Link>
            <a
              href="/#faq"
              className="block py-2 text-white hover:text-purple-200 font-semibold"
              onClick={closeMobileMenu}
            >
              FAQ
            </a>
            <button
              onClick={() => {
                window.open(WEBINAR_CONFIG.url, '_blank');
                closeMobileMenu();
              }}
              className="mt-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg w-full"
            >
              {WEBINAR_CONFIG.text}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
